/**
 * Shared building blocks used across the marketing pages.
 * All components are named exports so pages can import selectively.
 */
import React from "react";
import { Link } from "react-router-dom";
import { CP_BENEFITS } from "@/components/PartnerProgramme";
import {
    ShieldCheck, BadgeCheck, Landmark, IndianRupee, HeartHandshake, Banknote,
    FileCheck2, Globe2, Search, ScrollText, Lightbulb, CalendarCheck, Gift,
    Award, Target, TrendingUp, Users, Building2, MapPinned,
    ArrowRight, Check,
} from "lucide-react";

// ---------- 8 USP navy strip ----------
export const HOME_USPS = [
    { icon: ShieldCheck,   l: "Legally Verified",     s: "100% Verified"     },
    { icon: BadgeCheck,    l: "RERA Verified",        s: "All Projects"      },
    { icon: Landmark,      l: "Direct from Builder",  s: "Best Price"        },
    { icon: IndianRupee,   l: "Zero Hidden Charges",  s: "Transparent Deals" },
    { icon: Banknote,      l: "Home Loan Assistance", s: "Fast Loan Approval" },
    { icon: FileCheck2,    l: "Registration Support", s: "End-to-End Help"   },
    { icon: Globe2,        l: "NRI Investment Desk",  s: "Global Assistance" },
    { icon: HeartHandshake,l: "Dedicated RM",         s: "Personal Support"  },
];

function USPItem({ u, i }) {
    return (
        <div data-testid={`usp-v2-${i}`} className="flex flex-col items-center text-center gap-1.5 min-w-[128px]">
            <span className="w-10 h-10 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center shrink-0">
                <u.icon size={16} strokeWidth={1.8}/>
            </span>
            <div className="text-[9px] md:text-[10px] uppercase tracking-[0.08em] font-bold leading-tight whitespace-nowrap">{u.l}</div>
            <div className="text-[9px] text-white/50 leading-tight whitespace-nowrap">{u.s}</div>
        </div>
    );
}

export function USPStripV2() {
    return (
        <section data-testid="usp-strip-v2" className="bg-[var(--ebg-black)] text-white border-y border-white/10">
            {/* Desktop / tablet: 8-column grid with vertical dividers */}
            <div className="hidden md:block">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5">
                    <div className="grid md:grid-cols-4 lg:grid-cols-8 divide-x divide-white/8">
                        {HOME_USPS.map((u, i) => (
                            <div key={i} className={`px-2 ${i === 0 ? "md:pl-0" : ""} ${i === HOME_USPS.length - 1 ? "md:pr-0" : ""}`}>
                                <USPItem u={u} i={i}/>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Mobile: infinite auto-scrolling marquee — premium ticker feel */}
            <div className="md:hidden overflow-hidden py-5">
                <div className="flex items-center gap-8 whitespace-nowrap animate-scroll-x">
                    {[...HOME_USPS, ...HOME_USPS].map((u, i) => (
                        <div key={i} className="shrink-0 flex items-center gap-3">
                            <span className="w-9 h-9 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center">
                                <u.icon size={14} strokeWidth={1.8}/>
                            </span>
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase tracking-[0.14em] font-bold leading-tight">{u.l}</span>
                                <span className="text-[9px] text-white/50 leading-tight mt-0.5">{u.s}</span>
                            </div>
                            <span className="ml-6 w-1 h-1 rounded-full bg-[var(--ebg-gold)]/40"/>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------- 6-stat light strip ----------
export function StatsStripV2({ items }) {
    return (
        <section data-testid="stats-strip" className="bg-[var(--ebg-ivory)] border-y border-black/8">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-8 gap-x-4">
                {items.map((s, i) => (
                    <div key={i} data-testid={`stat-${i}`} className="text-center md:text-left">
                        <div className="display-num text-3xl md:text-[36px] text-black leading-none tabular-nums">{s.v}</div>
                        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-black/55 mt-2">{s.l}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------- Process Timeline (5 or 6 steps) ----------
export function ProcessTimeline({ steps, variant = "dark", eyebrow = "How It Works", heading = "A Simple. Transparent. Secure Process." }) {
    const isDark = variant === "dark";
    return (
        <section
            data-testid="process-timeline"
            className={isDark ? "bg-[var(--ebg-black)] text-white" : "bg-white text-black"}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{eyebrow}</div>
                    <h2 className={`font-serif text-3xl md:text-5xl mt-3 leading-[1.05] ${isDark ? "text-white" : "text-black"}`}>{heading}</h2>
                </div>
                <div className="mt-14 grid gap-6 md:gap-0" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
                    {steps.map((s, i) => (
                        <div key={i} data-testid={`process-step-${i}`} className="relative flex flex-col items-center text-center px-3">
                            {/* connector line */}
                            {i < steps.length - 1 && (
                                <span
                                    className={`hidden md:block absolute top-8 left-1/2 w-full h-px ${isDark ? "bg-white/15" : "bg-black/15"}`}
                                    style={{ zIndex: 0 }}
                                />
                            )}
                            <div className="relative z-10 w-16 h-16 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center shadow-lg">
                                <s.icon size={22} strokeWidth={1.7}/>
                            </div>
                            <div className={`display-num text-[var(--ebg-gold)] text-sm mt-4 tabular-nums`}>0{i + 1}</div>
                            <h3 className={`font-serif text-lg md:text-xl mt-2 ${isDark ? "text-white" : "text-black"}`}>{s.t}</h3>
                            <p className={`text-[12px] leading-relaxed mt-2 ${isDark ? "text-white/60" : "text-black/60"}`}>{s.d}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export const HOME_PROCESS_STEPS = [
    { icon: Search,        t: "Search",              d: "Explore verified projects tailored to your goals." },
    { icon: ShieldCheck,   t: "Legal Verification",  d: "120+ point due diligence on every property." },
    { icon: CalendarCheck, t: "Site Visit",          d: "Coordinated visits at your convenience." },
    { icon: Lightbulb,     t: "Expert Guidance",     d: "ROI, financing and negotiation support." },
    { icon: Award,         t: "Secure Property",     d: "Documentation, registration & possession — done." },
];

export const SERVICES_PROCESS_STEPS = [
    { icon: Search,        t: "Discover",       d: "Share your needs. We shortlist the best match for you." },
    { icon: ShieldCheck,   t: "Verify",         d: "Our legal team conducts 120+ checks for complete due diligence." },
    { icon: Lightbulb,     t: "Advise",         d: "Personalised advice with investment & risk insights." },
    { icon: CalendarCheck, t: "Visit & Decide", d: "Book site visits and get complete project information." },
    { icon: HeartHandshake,t: "Secure",         d: "We handle documentation, registration & loan process." },
    { icon: Gift,          t: "Celebrate",      d: "Move in with peace of mind. We stay connected." },
];

// ---------- 5 trust badges dark strip ----------
export const SERVICE_TRUST_BADGES = [
    { icon: FileCheck2,  t: "Legally Verified", sub: "120+ Checkpoints" },
    { icon: BadgeCheck,  t: "RERA Verified",    sub: "All Projects" },
    { icon: ScrollText,  t: "Title Verified",   sub: "Clear & Marketable" },
    { icon: HeartHandshake, t: "Builder Approved", sub: "Direct Partnerships" },
    { icon: Landmark,    t: "Bank Approved",    sub: "Financing Ready" },
];

export function TrustBadgesDark({ badges = SERVICE_TRUST_BADGES }) {
    return (
        <section data-testid="trust-badges-dark" className="bg-[#0a0d18] text-white">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-4">
                    <div className="w-16 h-16 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center mb-5">
                        <ShieldCheck size={26}/>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">EBG Trust</div>
                    <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-[1.05]">Trust is<br/>Our Foundation.</h2>
                    <p className="text-white/60 mt-5 max-w-sm leading-relaxed text-[15px]">
                        Every service we offer is backed by our proprietary verification framework.
                    </p>
                </div>
                <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-3">
                    {badges.map((b, i) => (
                        <div key={i} data-testid={`trust-badge-dark-${i}`} className="text-center border border-white/10 bg-white/5 py-6 px-3 hover:border-[var(--ebg-gold-line)] transition-colors">
                            <span className="mx-auto w-12 h-12 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center">
                                <b.icon size={18}/>
                            </span>
                            <div className="font-serif text-base md:text-lg mt-3">{b.t}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50 mt-1">{b.sub}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ---------- Builder Partners strip ----------
const BUILDER_LOGOS = ["Carlton Estates", "Brillen", "Flamingo", "Krillam", "Chaturvatika", "GB Realty"];

export function BuilderPartnersStrip({ eyebrow = "Our Builder Partners", heading = "Trusted by India's Finest." }) {
    return (
        <section data-testid="builder-partners" className="bg-white border-y border-black/8">
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16">
                <div className="text-center mb-10">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{eyebrow}</div>
                    <h2 className="font-serif text-2xl md:text-3xl mt-2">{heading}</h2>
                </div>
                <div className="overflow-hidden">
                    <div className="flex items-center gap-10 md:gap-16 animate-scroll-x whitespace-nowrap">
                        {[...BUILDER_LOGOS, ...BUILDER_LOGOS, ...BUILDER_LOGOS, ...BUILDER_LOGOS].map((name, i) => (
                            <div key={i} data-testid={`builder-logo-${i % BUILDER_LOGOS.length}`} className="shrink-0 grayscale opacity-50 hover:opacity-100 hover:grayscale-0 transition-all">
                                <span className="font-serif text-xl md:text-2xl tracking-wide text-black/70">{name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- Testimonial with backdrop ----------
export function TestimonialSection({
    image = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=80",
    quote = "The EBG Realty team made our home-buying journey smooth, secure and truly delightful. Every detail was legally verified and every promise was kept — we didn't have to second-guess a single step.",
    author = "Rohit Sharma",
    role = "Happy Homeowner · Mumbai",
}) {
    return (
        <section data-testid="testimonial-section" className="relative bg-[#0a0d18] text-white overflow-hidden">
            <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25"/>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/70 to-transparent"/>
            <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-32 max-w-3xl">
                <div className="text-[var(--ebg-gold)] font-serif text-7xl leading-none">&ldquo;</div>
                <blockquote className="font-serif text-2xl md:text-3xl lg:text-[36px] leading-[1.25] max-w-3xl mt-4">
                    {quote}
                </blockquote>
                <div className="mt-8 flex items-center gap-4">
                    <div className="w-12 h-px bg-[var(--ebg-gold)]"/>
                    <div>
                        <div className="font-bold">{author}</div>
                        <div className="text-white/60 text-sm">{role}</div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- Channel Partner (homepage) ----------

const CP_PORTRAIT = "/home/channel-partner.png";

export function ChannelPartnerCTA() {
    return (
        <section
            data-testid="channel-partner-cta"
            className="relative bg-[var(--ebg-ivory)] text-black overflow-hidden"
        >
            {/* subtle gold accent bar */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--ebg-gold)]/40 to-transparent"/>

            <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-14 md:py-16 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                {/* ---------- LEFT: Copy + CTAs ---------- */}
                <div className="lg:col-span-4">
                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Channel Partners</div>
                    <h2 className="font-serif text-3xl md:text-4xl lg:text-[40px] leading-[1.05] mt-3" data-testid="cp-home-heading">
                        Become an EBG Realty<br/><em className="not-italic text-[var(--ebg-gold)]">Channel Partner</em>
                    </h2>
                    <p className="text-black/70 mt-4 font-serif text-lg" data-testid="cp-home-tagline">Your Network + Our Platform = Bigger Business</p>

                    <div className="mt-6 grid grid-cols-2 border border-black/10 bg-white" data-testid="cp-home-types">
                        <div className="p-4 border-r border-black/10">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold">Channel Partner</div>
                            <div className="text-[12px] text-black/60 mt-1">Property Upload</div>
                        </div>
                        <div className="p-4 bg-[#0a0d18] text-white">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)]">Premium Channel Partner</div>
                            <div className="text-[12px] text-white/70 mt-1">Additional Training + EBG Brands, subject to eligibility and confirmation</div>
                        </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Link
                            to="/partner-registration"
                            data-testid="cp-become-btn"
                            className="bg-[var(--ebg-gold)] text-black px-6 py-3.5 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors inline-flex items-center gap-2"
                        >
                            Register as Channel Partner <ArrowRight size={12}/>
                        </Link>
                    </div>
                </div>

                {/* ---------- MIDDLE: Approved programme benefits ---------- */}
                <ul className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="cp-home-benefits">
                    {CP_BENEFITS.map((t, i) => (
                        <li key={t} data-testid={`cp-feature-${i}`} className="bg-white border border-black/8 px-4 py-3 flex items-center gap-3 text-[13.5px] hover:border-[var(--ebg-gold)]/60 transition-colors">
                            <span className="w-7 h-7 rounded-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] flex items-center justify-center shrink-0"><Check size={13}/></span>
                            {t}
                        </li>
                    ))}
                </ul>

                {/* ---------- RIGHT: Portrait ---------- */}
                <div className="lg:col-span-3 relative hidden lg:block" data-testid="cp-portrait">
                    <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                            src={CP_PORTRAIT}
                            alt="EBG Realty Channel Partner"
                            className="w-full h-full object-cover object-top"
                        />
                        {/* gold corner accent */}
                        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[var(--ebg-gold)]"/>
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[var(--ebg-gold)]"/>
                        {/* testimonial-style badge */}
                        <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)]">Rakesh Malhotra</div>
                            <div className="text-[11px] text-black/60 mt-0.5">EBG Partner · Mumbai · 6 yrs</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* subtle gold accent bar bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--ebg-gold)]/40 to-transparent"/>
        </section>
    );
}

// ---------- Category chips row (Insights + Property listing top bar) ----------
export function CategoryChips({ items, activeKey, onSelect, testidPrefix = "chip" }) {
    return (
        <div data-testid={`${testidPrefix}-row`} className="flex overflow-x-auto no-scrollbar gap-3 md:gap-4 pb-2">
            {items.map((c) => {
                const active = c.key === activeKey;
                return (
                    <button
                        key={c.key}
                        data-testid={`${testidPrefix}-${c.key}`}
                        onClick={() => onSelect(c.key)}
                        className={`group shrink-0 flex items-center gap-3 px-5 py-3 border transition-all ${
                            active
                                ? "border-black bg-black text-white"
                                : "border-black/12 bg-white text-black hover:border-black"
                        }`}
                    >
                        {c.icon && (
                            <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                                active ? "bg-[var(--ebg-gold)] text-black" : "bg-[var(--ebg-gold)]/12 text-[var(--ebg-gold)]"
                            }`}>
                                <c.icon size={15}/>
                            </span>
                        )}
                        <div className="text-left">
                            <div className={`text-[12px] font-bold uppercase tracking-[0.16em] ${active ? "text-white" : "text-black"}`}>{c.label}</div>
                            {c.sub && (
                                <div className={`text-[10px] ${active ? "text-white/60" : "text-black/45"} mt-0.5`}>{c.sub}</div>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

// ---------- Small utility: icons re-exported to help pages ----------
export { Award, Target, TrendingUp, Users, Building2, MapPinned };
